<template>
  <div>
    <div v-if="effectsVisibility === false && aiFeatureVisibility === true" class="row">
      <div class="col-xs-12">
        <div class="read-only-warning-banner">
          <i class="fa fa-lock"/>
          <div class="warning-content">
            <strong>The statement below cannot be edited, since it was created using a previous version of the Present Levels Wizard.</strong>
            <p>You may still copy the statement text, as well as view aligned assessments and aligned goals associated with the statement.</p>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-xs-12 col-md-9 col-lg-8">
        <div class="primary-action-button-group">
          <CopyButton
            v-if="!developPLWPermissions.block"
            @copied="statementCopied"
            btn-class="btn btn-default default-action-button"
            :copy-text="generatedPresentLevels"
            success-text="Present levels statement copied"
            error-text="Error copying text. Use Ctrl+C instead.">Copy Statement</CopyButton>
          <button
            v-else
            class="btn btn-default default-action-button"
            @click="blockCopyButton">Copy Statement</button>
          <button
            v-if="showEditButton"
            type="button"
            class="btn btn-primary primary-action-button"
            @click="editStatements">Edit Statements</button>
        </div>
        <div class="mdc-card statement-card-wrapper" :class="{ 'read-only-statement': effectsVisibility === false && aiFeatureVisibility === true }">
          <div v-if="effectsVisibility === false && aiFeatureVisibility === true && showHoverTooltip" class="hover-tooltip">
            <i class="fa fa-lock"/> The statement below cannot be edited, since it was created using a previous version of the Present Levels Wizard.
          </div>
          <section
            class="mdc-card__supporting-text gb-text-2"
            :class="{ 'not-allowed-cursor': effectsVisibility === false && aiFeatureVisibility === true }"
            @mouseenter="effectsVisibility === false && aiFeatureVisibility === true && (showHoverTooltip = true)"
            @mouseleave="showHoverTooltip = false">
            <WikiText
              class="gb-text-3 pl-editable-textarea fs-block"
              :locked="shouldBlur"
              :text="generatedPresentLevels"/>
          </section>
        </div>
      </div>
      <div class="col-xs-12 col-md-3 col-lg-4">
        <save-to-library-panel
          :content-uuid="uuid"
          :content-type="contentType"
          :content-last-save="lastUpdate && lastUpdate.toString()"
          :content-saving="saving"
          :collection-uuid="savedPLCollectionUuid"
          :collection-name="savedPLCollectionName"
          :folder-users="folderUsers"
          :content="{demo: false}"
          :hide-save-button="effectsVisibility === false && aiFeatureVisibility === true"
          @saved:success="refreshCurrentContentFolder"
          @removed:success="handleRemoveSuccess" />
      </div>
    </div>
  </div>
</template>

<script>
import { populatePlaceholderText } from '@/api/presentlevels'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import WikiText from '@/components/WikiText'
import CopyTrackWrapper from '@/components/CopyTrackWrapper'
import CopyButton from '@/components/CopyButton'
import SaveToLibraryPanel from '@/components/presentlevels/SaveToLibraryPanel'
import PresentLevelsConclusionTabs from '@/components/presentlevels/PresentLevelsConclusionTabs'
import { mapActions, mapGetters, mapState } from 'vuex'
import { authorizationCheckMixin } from '@/router/authorization_mixin'
import { blockPlwMixin } from '@/components/presentlevels/block_plw_mixin'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { SEL_DEFAULT_AREA_PLW, SEL_REPLACEMENT_STRATEGIES_PLW } from '@/contentprofile/no_sel_mixins'
import { toolkitUrls } from '@/links'

const RESELECT_MODES = Object.freeze({
  BACK_MODE: Symbol('Previous route was the present statement selection view.'),
  REPLACE_MODE: Symbol('Direct navigation or through library to present level conclusion view.')
})

export default {
  name: 'PresentlevelsConclusionStatement',
  components: {
    WikiText,
    CopyTrackWrapper,
    CopyButton,
    SaveToLibraryPanel,
    PresentLevelsConclusionTabs
  },
  mixins: [authorizationCheckMixin, blockPlwMixin],
  props: {
    uuid: {
      required: true,
      type: String
    },
    presentLevelStatement: {
      type: String,
      default: ''
    },
    studentName: {
      type: String,
      default: ''
    },
    studentUuid: {
      type: String,
      default: ''
    },
    enrolledGrade: {
      type: String,
      default: ''
    },
    selectedSubjects: {
      type: Array,
      default: ()=> []
    }
  },
  data() {
    return {
      reselectMode: RESELECT_MODES.REPLACE_MODE,
      contentType: CONTENT_TYPES.PRESENTLEVEL,
      showHoverTooltip: false
    }
  },
  computed: {
    ...mapGetters('user', ['userId']),
    ...mapGetters('library', ['canEditCurrentContent']),
    ...mapState('library', {
      savedPLCollectionUuid: state => state.currentContentFolder.folderUuid,
      savedPLCollectionName: state => state.currentContentFolder.folderName,
      folderUsers: state => state.currentContentFolder.folderUsers.getFolderMembers()
    }),
    ...mapState('presentlevels', {
      saving: state => state.saving,
      lastUpdate: state => state.lastUpdate
    }),
    ...mapGetters('visibility',['selVisibility', 'effectsVisibility', 'aiFeatureVisibility']),
    generatedPresentLevels: function() {
      let presentLevelsDisplay = wikiToPlainText(this.presentLevelStatement)
      if (!this.selVisibility) {
        presentLevelsDisplay = presentLevelsDisplay.replaceAll(SEL_DEFAULT_AREA_PLW, SEL_REPLACEMENT_STRATEGIES_PLW)
        presentLevelsDisplay = presentLevelsDisplay.replaceAll(SEL_DEFAULT_AREA_PLW.toUpperCase(), SEL_REPLACEMENT_STRATEGIES_PLW.toUpperCase())
      }

      // Replace placeholder text
      if (this.studentName) {
        presentLevelsDisplay = populatePlaceholderText(presentLevelsDisplay, {
          studentName: this.studentName,
          studentGrade: this.enrolledGrade
        })
      } else {
        presentLevelsDisplay = 'Generating statement...'
      }
      return presentLevelsDisplay
    },
    shouldBlur() {
      // Users with aiFeatureVisibility=true should never see blur on v1 Present Levels
      // They can view the content (read-only) but it should not be blurred
      if (this.aiFeatureVisibility === true) {
        return false;
      }
      // For users without AI features, apply normal blur permissions
      return this.blurPLWPermissions.blur;
    },
    isReadOnly() {
      // Users with aiFeatureVisibility=false can always edit
      if (this.aiFeatureVisibility === false) {
        return false;
      }
      // Users with effectsVisibility=false AND aiFeatureVisibility=true are read-only
      if (this.effectsVisibility === false && this.aiFeatureVisibility === true) {
        return true;
      }
      // Users with effectsVisibility=true can always edit
      if (this.effectsVisibility === true) {
        return false;
      }
      // For other users, check if saved to library and edit permissions
      return !!this.savedPLCollectionUuid && !this.canEditCurrentContent;
    },
    showEditButton() {
      // Hide button for users with effectsVisibility=false AND aiFeatureVisibility=true (read-only)
      if (this.effectsVisibility === false && this.aiFeatureVisibility === true) {
        return false;
      }
      // Hide button if user cannot edit (e.g., contributors)
      if (this.savedPLCollectionUuid && !this.canEditCurrentContent) {
        return false;
      }
      // Show button when not read-only
      return !this.isReadOnly;
    }
  },
  methods: {
    ...mapActions('presentlevels', ['generateGoalShortcutData', 'generateStrategyShortcutData']),
    ...mapActions('library', ['fetchCurrentContentFolder']),
    editStatements() {
      this.$sendEvent('Clicked Edit Statements from Drafted')
      if (this.shouldBlockPLW()) {
        return
      }
      if (this.reselectMode === RESELECT_MODES.BACK_MODE) {
        this.$router.go(-1)
      } else if (this.reselectMode === RESELECT_MODES.REPLACE_MODE) {
        this.$router.replace({ name: 'PresentlevelsResults', params: { uuid: this.uuid } })
      }
    },
    newPresentLevels() {
      this.$sendEvent('Clicked New Present Levels from Drafted')
      if (this.shouldBlockPLW()) {
        return
      }
      this.$router.push({ name: 'PresentLevelsNewWizard' })
    },
    stopEditing(text) {
      this.generatedPresentLevels = text
    },
    statementCopied() {
      this.$sendEvent('Clicked Copy Statement from Drafted')
    },
    blockCopyButton() {
      this.statementCopied()
      this.shouldBlockPLW()
    },
    refreshCurrentContentFolder() {
      this.fetchCurrentContentFolder({
        userUuid: this.userId,
        contentUuid: this.uuid,
        contentType: this.contentType
      })
    },
    handleRemoveSuccess(resp) {
      this.refreshCurrentContentFolder()
      if (this.effectsVisibility === false && this.aiFeatureVisibility === true) {
        window.location.href = toolkitUrls.libraryFolder(this.savedPLCollectionUuid, true)
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // If you came from the Present Levels Statement Selection View, then just go back
      // Otherwise, replace the current frame in the history with the Statement Selection View
      if (from.name === 'PresentlevelsResults') {
        vm.reselectMode = RESELECT_MODES.BACK_MODE
      } else {
        vm.reselectMode = RESELECT_MODES.REPLACE_MODE
      }
    })
  }
}
</script>

<style scoped lang="scss">
@import '@material/card/mdc-card';

.primary-action-button-group {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-top: 10px;
  .default-action-button,
  .primary-action-button {
    display: inline-block;
    text-decoration: none;
  }
  .btn-light {
    background-color: white;
    border-color: lightgray;
  }
  button{
    margin-left: 4px;
  }
}

h2 {
  line-height: 36px;
  text-align: center;
  margin-bottom: 20px;

  .student-name {
    font-style: italic;
    color: #3c9ad5;
  }
}

.mdc-card {
  margin-top: 15px;
  padding: 15px;
  background: transparent;
  box-shadow: none;
  border: 1px solid lightgray;
}

textarea {
  background: #ffc;
  padding: 15px;
  color: #000;
}

.pl-editable-textarea {
  background: white;
  padding: 0.5em !important;
}

.mdc-card.read-only-statement {
  background-color: #F1F1F1;

  .mdc-card__supporting-text {
    background-color: #F1F1F1;
  }

  .pl-editable-textarea {
    background: #F1F1F1;
  }
}

.statement-card-wrapper {
  position: relative;
}

.hover-tooltip {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background-color: #000;
  color: #fff;
  padding: 12px 15px;
  z-index: 10;
  font-size: 14px;
  display: flex;
  align-items: center;

  .fa-lock {
    margin-right: 8px;
  }
}

.not-allowed-cursor {
  cursor: not-allowed;

  * {
    cursor: not-allowed;
  }
}

.read-only-warning-banner {
  display: flex;
  align-items: flex-start;
  background-color: #d9edf7;
  border: 1px solid #bce8f1;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
  color: #31708f;

  .fa-lock {
    font-size: 20px;
    margin-right: 15px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .warning-content {
    flex: 1;

    strong {
      display: block;
      margin-bottom: 8px;
      font-size: 16px !important;
    }

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
    }
  }
}
</style>
